import * as React from "react";
import tw, { styled } from "twin.macro";
import { Popover, Transition } from "@headlessui/react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import Text from "app/styles/Text";
import { ActiveProfile } from "app/models/profile";
import { DEFAULT_PROFILE_IMG, HOME_PATH } from "app/constants/variables";
import { useProfile } from "app/providers/ProfileProvider";
import { ReactComponent as ProfileIcon } from "app/assets/icons/profile-outline.svg";
import { ReactComponent as DoubleProfileIcon } from "app/assets/icons/user-double.svg";
import { ReactComponent as LogoutIcon } from "app/assets/icons/logout.svg";
import { ReactComponent as InfoIcon } from "app/assets/icons/info.svg";
import { ReactComponent as CancelIcon } from "app/assets/icons/cancel.svg";
import { ReactComponent as PlayStore } from "app/assets/images/play-store.svg";
import { ReactComponent as AppStore } from "app/assets/images/app-store.svg";
import { ReactComponent as CheckIcon } from "app/assets/icons/check.svg";
import { ReactComponent as PlusIcon } from "app/assets/icons/plus.svg";
import { ReactComponent as CodeIcon } from "app/assets/icons/code.svg";
import { ReactComponent as RightAngleArrowIcon } from "app/assets/icons/right-angle-arrow.svg";
import { gotoSignout } from "app/lib/logout";
import {
  useActiveProfile,
  useSetActiveProfile,
} from "app/providers/ActiveProfileProvider";
import useGetProfiles from "app/hooks/useGetProfiles";
import { canView } from "app/lib/permissions";
import {
  useActiveCustomerAccount,
  useSetActiveCustomer,
  useIsCustomerMode,
} from "app/providers/ActiveCustomerAccountProvider";
import { ReactComponent as UserIcon } from "app/assets/icons/user.svg";
import {
  BUSINESS_ONLY_QUERIES,
  PERSONAL_ONLY_QUERIES,
} from "app/constants/commonQueries";

export default function ProfileDropdown() {
  const imageRef = React.useRef<HTMLImageElement>(null);

  const navigate = useNavigate();

  const profile = useProfile();

  const activeProfile = useActiveProfile();

  const activeCustomerAccount = useActiveCustomerAccount();

  const setActiveCustomer = useSetActiveCustomer();

  const isCustomerMode = useIsCustomerMode();

  // console.log({ activeCustomerAccount, activeProfile, isCustomerMode });

  const setActiveProfile = useSetActiveProfile();

  const { profiles } = useGetProfiles();

  const queryClient = useQueryClient();

  const canViewApiKeys =
    activeProfile?.profile_type === "business"
      ? canView("api_keys:*", activeProfile?.permissions as string[]) ||
        canView("api_keys:read", activeProfile?.permissions as string[])
      : false;
  const canViewWebhooks =
    activeProfile?.profile_type === "business"
      ? canView("webhooks:*", activeProfile?.permissions as string[]) ||
        canView("webhooks:read", activeProfile?.permissions as string[])
      : false;

  const showDeveloperTools = canViewApiKeys || canViewWebhooks;

  if (!profile || profile.status === "pending" || !activeProfile || !profiles)
    return null;

  const logout = () => gotoSignout();

  const sortedProfiles = profiles.sort((a, b) => {
    return (
      (a.profile_type === (profile.business_default ? "business" : "personal")
        ? -1
        : 1) -
      (b.profile_type === (profile.business_default ? "business" : "personal")
        ? -1
        : 1)
    );
  });

  const onActiveProfileChanged = async (p: ActiveProfile, cb: VoidFunction) => {
    if (p.id === activeProfile.id && !activeCustomerAccount) {
      setActiveCustomer(null);
      cb();
      return;
    }

    try {
      setActiveProfile(p);

      setActiveCustomer(null);

      if (p.profile_type === "personal") {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            !BUSINESS_ONLY_QUERIES.includes(query.queryKey[0] as string),
        });
      } else {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            !PERSONAL_ONLY_QUERIES.includes(query.queryKey[0] as string),
        });
      }

      cb();

      navigate(HOME_PATH);
    } catch (error) {
      console.log(error);
    }
  };

  const accountInitial = isCustomerMode ? (
    <>
      {activeCustomerAccount?.type === "individual" ? (
        <>{(activeCustomerAccount?.first_name?.[0] || " ").toUpperCase()}</>
      ) : (
        ((activeCustomerAccount?.business_name ?? "")[0] || " ").toUpperCase()
      )}
    </>
  ) : (
    <>{activeProfile.name[0].toUpperCase()}</>
  );

  return (
    <Popover tw="relative flex">
      {({ open, close }) => (
        <>
          <Popover.Button as={Container} type="button">
            {activeProfile.profile_type === "business" && (
              <InitialSpan tw="rounded-full mr-[10px] w-[40px] h-[40px] text-[16px] flex-shrink-0 relative">
                {accountInitial}

                {isCustomerMode && (
                  <span tw="flex items-center justify-center rounded-[99px] p-[4px] bg-text-high border border-white absolute bottom-0 right-0">
                    <UserIcon tw="size-[8px] text-text-highInverted" />
                  </span>
                )}
              </InitialSpan>
            )}
            <img
              src={profile.avatar}
              alt={activeProfile.name}
              css={[
                open && tw`mobile:hidden`,
                activeProfile.profile_type !== "personal" && tw`hidden`,
              ]}
              onError={() => {
                if (imageRef.current) {
                  imageRef.current.src = DEFAULT_PROFILE_IMG;
                }
              }}
              ref={imageRef}
            />

            <CancelIcon css={[tw`hidden`, open && tw`mobile:inline-block`]} />

            {isCustomerMode ? (
              <div tw="flex flex-col gap-[4px]">
                <Text mediumBold>
                  {activeCustomerAccount?.type === "individual" ? (
                    <>
                      {activeCustomerAccount?.first_name}{" "}
                      {activeCustomerAccount?.last_name}
                    </>
                  ) : (
                    activeCustomerAccount?.business_name ?? ""
                  )}
                </Text>

                <Text xSmall mediumBold subdued tw="text-left">
                  Customer account
                </Text>
              </div>
            ) : (
              <Text mediumBold>{activeProfile.name}</Text>
            )}
          </Popover.Button>

          {/* @ts-ignore */}
          <Transition>
            {/* @ts-ignore */}
            <Transition.Child
              as={React.Fragment}
              enter="transition-opacity ease-out duration-500"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Popover.Overlay as={Overlay} />
            </Transition.Child>

            <Transition.Child
              as={React.Fragment}
              enter="transition duration-300 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Popover.Panel as={Dropdown}>
                <div tw="flex flex-col items-center pb-[16px]">
                  {activeProfile.profile_type === "business" && (
                    <InitialSpan tw="rounded-full mr-[10px] w-[64px] h-[64px] text-[24px] flex-shrink-0 relative">
                      {accountInitial}

                      {isCustomerMode && (
                        <span tw="flex items-center justify-center rounded-[99px] p-[6px] bg-text-high absolute bottom-0 right-0">
                          <UserIcon tw="size-[12px] text-text-highInverted shrink-0" />
                        </span>
                      )}
                    </InitialSpan>
                  )}
                  <img
                    src={profile.avatar}
                    alt={activeProfile.name}
                    css={[
                      activeProfile.profile_type !== "personal" && tw`hidden`,
                    ]}
                    onError={() => {
                      if (imageRef.current) {
                        imageRef.current.src = DEFAULT_PROFILE_IMG;
                      }
                    }}
                    ref={imageRef}
                  />

                  {isCustomerMode ? (
                    <Text medium tw="mt-[16px] mb-[8px]">
                      {activeCustomerAccount?.type === "individual" ? (
                        <>
                          {activeCustomerAccount?.first_name}{" "}
                          {activeCustomerAccount?.last_name}
                        </>
                      ) : (
                        activeCustomerAccount?.business_name ?? ""
                      )}
                    </Text>
                  ) : (
                    <Text medium tw="mt-[16px] mb-[8px]">
                      {activeProfile.name}
                    </Text>
                  )}

                  {isCustomerMode ? (
                    <Text subdued>Customer account</Text>
                  ) : (
                    <>
                      {activeProfile.profile_type === "business" ? (
                        <Text subdued>Business account</Text>
                      ) : (
                        <Text tw="text-primary70 dark:text-primary50">
                          @{profile.username}
                        </Text>
                      )}
                    </>
                  )}
                </div>

                <div className="divider" tw="py-[16px]">
                  {activeProfile.profile_type === "personal" ? (
                    <DropdownLink as={Link} to="/profile" onClick={close}>
                      <ProfileIcon /> Profile
                    </DropdownLink>
                  ) : (
                    activeProfile.role_name !== "Viewer" && (
                      <DropdownLink as={Link} to="/profile" onClick={close}>
                        <ProfileIcon /> Profile
                      </DropdownLink>
                    )
                  )}

                  {activeProfile.profile_type === "business" &&
                    !isCustomerMode &&
                    canView(
                      "team_management:*",
                      activeProfile.permissions as string[]
                    ) && (
                      <DropdownLink
                        as={Link}
                        to="/profile/team"
                        onClick={close}
                      >
                        <DoubleProfileIcon tw="dark:[&>path]:fill-[#fff]" />{" "}
                        Team
                      </DropdownLink>
                    )}

                  <DropdownLink
                    href="https://support.busha.co/en/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <InfoIcon /> Help center
                  </DropdownLink>

                  {showDeveloperTools && (
                    <DropdownLink
                      as={Link}
                      to="/profile/developer-tools"
                      onClick={close}
                    >
                      <CodeIcon /> Developer Tools
                    </DropdownLink>
                  )}
                </div>

                <div className="divider" tw="py-[12px] space-y-[8px]">
                  {sortedProfiles.map((_profile) => {
                    const isActive = _profile.id === activeProfile.id;

                    return (
                      <div key={_profile.id}>
                        <div
                          role="button"
                          tw="flex items-center gap-[12px] hover:bg-[#F9F9F9] dark:hover:bg-greyScale90 rounded-[8px] p-[4px] overflow-hidden"
                          onClick={() =>
                            onActiveProfileChanged(_profile, close)
                          }
                        >
                          <InitialSpan tw="relative">
                            {_profile.name[0].toUpperCase()}

                            {isActive && isCustomerMode && (
                              <span tw="w-[1px] h-[50%] bg-text-high absolute -bottom-1/2 left-[20px]" />
                            )}
                          </InitialSpan>
                          <div tw="flex justify-between items-center flex-1">
                            <div tw="flex flex-col gap-[4px]">
                              <Text medium>{_profile.name}</Text>

                              <Text tw="text-textMid" subdued>
                                {_profile.profile_type === "personal"
                                  ? "Personal account"
                                  : "Business account"}
                              </Text>
                            </div>

                            {isActive && !isCustomerMode && (
                              <CheckIcon tw="dark:[&>path]:fill-[#fff]" />
                            )}
                          </div>
                        </div>

                        {isCustomerMode && isActive && (
                          <div tw="flex items-center gap-[12px] hover:bg-[#F9F9F9] dark:hover:bg-greyScale90 rounded-[8px] p-[4px] overflow-hidden">
                            <div tw="flex items-start justify-end w-[40px] h-[36px] relative">
                              <span tw="w-[1px] h-[50%] bg-text-high absolute -top-1/2 left-[20px]" />
                              <RightAngleArrowIcon tw="w-[21.8px] h-[20px] shrink-0" />
                            </div>
                            <div tw="flex justify-between items-center flex-1">
                              <div tw="flex flex-col gap-[4px]">
                                {activeCustomerAccount?.type ===
                                "individual" ? (
                                  <Text medium>
                                    {activeCustomerAccount?.first_name}{" "}
                                    {activeCustomerAccount?.last_name}
                                  </Text>
                                ) : (
                                  <Text medium>
                                    {activeCustomerAccount?.business_name}
                                  </Text>
                                )}

                                <Text tw="text-textMid" subdued>
                                  Customer account (
                                  {activeCustomerAccount?.type === "individual"
                                    ? "Personal"
                                    : "Business"}
                                  )
                                </Text>
                              </div>

                              <CheckIcon tw="dark:[&>path]:fill-[#fff]" />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <Link
                    to="/create-business"
                    tw="flex items-center gap-[12px] hover:bg-[#F9F9F9] dark:hover:bg-greyScale90 rounded-[8px] p-[4px]"
                  >
                    <InitialSpan>
                      <PlusIcon tw="w-[20px] h-[20px]" />
                    </InitialSpan>
                    <div tw="flex justify-between items-center flex-1">
                      <div>
                        <Text medium>Open a business account</Text>
                      </div>
                    </div>
                  </Link>
                </div>

                {/* <div className="divider" tw="py-[16px]">
                  <div tw="pb-[8px] pl-[8px]">
                    <Text
                      semiBold
                      medium
                      tw="text-[rgba(0, 0, 0, 0.6)] dark:text-[rgba(255, 255, 255, 0.6)]"
                    >
                      Explore other Busha products
                    </Text>
                  </div>

                  <DropdownLink
                    href="https://www.busha.co/commerce"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <GlobeIcon /> Accept payments for goods
                  </DropdownLink>
                </div> */}

                <div className="divider" tw="py-[16px]">
                  <div tw="pb-[8px] pl-[8px]">
                    <Text
                      semiBold
                      medium
                      tw="text-[rgba(0, 0, 0, 0.6)] dark:text-[rgba(255, 255, 255, 0.6)]"
                    >
                      Download our app
                    </Text>
                  </div>

                  <DownloadApp>
                    <a
                      aria-label="Play store"
                      title="Play store"
                      href="https://play.google.com/store/apps/details?id=co.busha.android&hl=en_US/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <PlayStore />
                    </a>

                    <a
                      aria-label="App store"
                      title="App store"
                      href="https://apps.apple.com/us/app/busha-trade-btc-eth-shib/id1450373493"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <AppStore />
                    </a>
                  </DownloadApp>
                </div>

                <div className="divider" tw="pt-[16px]">
                  <DropdownLink
                    as="button"
                    tw="text-error50 h-[56px] font-semibold w-full"
                    onClick={logout}
                  >
                    <LogoutIcon /> Log out
                  </DropdownLink>
                </div>
              </Popover.Panel>
            </Transition.Child>
          </Transition>
        </>
      )}
    </Popover>
  );
}

const Container = styled.button`
  ${tw`flex items-center cursor-pointer outline-none`};

  > img {
    ${tw`w-[36px] h-[36px] mr-[8px] rounded-full mobile:m-0 object-cover`};
  }

  > svg {
    ${tw`w-[24px] h-[24px]`};

    path {
      ${tw`fill-current`};
    }
  }

  p {
    ${tw`mobile:hidden`};
  }
`;

const positionStyles = tw`absolute z-10 right-[-32px] mobile:right-[-16px] top-full`;

const Overlay = styled.div`
  ${positionStyles};
  ${tw`top-[-14px] mobile:top-full mobile:mt-[14px] w-screen h-screen mobile:h-page bg-[rgba(0, 0, 0, 0.25)] dark:bg-[rgba(50, 53, 61, 0.3)]`};
`;

const Dropdown = styled.div`
  ${positionStyles};
  ${tw`mt-[22px] mobile:mt-[14px] w-[375px] mobile:w-screen max-h-[calc(100vh - 108px)] overflow-y-auto`};

  ${tw`rounded-[8px] mobile:rounded-t-none rounded-b-[8px] bg-white p-[16px] mr-[16px] mobile:mr-0`};

  ${tw`dark:bg-black`};

  img {
    ${tw`w-[64px] h-[64px] rounded-full object-cover`};
  }

  .divider {
    ${tw`border-t border-t-[#F2F2F2]`};

    ${tw`dark:border-t-white12`};
  }
`;

const DropdownLink = styled.a<{ to?: string }>`
  ${tw`flex items-center text-[1.6rem] h-[44px]`};
  ${tw`hover:bg-greyScale10 dark:hover:bg-greyScale90 rounded-[8px] px-[12px]`};

  svg {
    ${tw`w-[24px] h-[24px] mr-[8px]`};

    path {
      fill-opacity: 1;
    }
  }
`;

const DownloadApp = styled.div`
  ${tw`flex gap-[12px] px-[12px] py-[16px]`}
`;

const InitialSpan = styled.span`
  ${tw`flex justify-center items-center w-[40px] h-[40px] bg-[#E5E5E5] rounded-[8px] text-[20px] font-medium dark:text-black95`}
`;
